import { Card, Checkbox, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { trueOrFalseEnum } from "utils/Enums";
import { errorAlert, infoAviso, infoWarning, successAlert, verifyVariable } from "utils/Functions";
import { get, post } from "utils/WebService";
import ForumCreateEdit from "./ForumCreateEdit";
import UploadArquivoForum from "./UploadArquivoForum";

export default function ForumConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
        estado: null,
        cidade: null,
        ativo: true
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);
    const [uploadArquivo, setUploadArquivo] = useState(false);
    const [cidadeList, setCidadeList] = useState([]);
    const [estadoList, setEstadoList] = useState([]);

    const [filters, setFilters] = useState({
        nome: '',
        estado: null,
        cidade: null,
        ativo: trueOrFalseEnum[1]
    });

    useEffect(() => {
        fetchData({ ...tableState });
        get(`/addresses/states`)
            .then(({ list }) => {
                setEstadoList(list);
            }).catch(() => {
                setEstadoList([]);
            });
    }, []);

    function selectEstadoCidade(estado) {
        get(`/addresses/cities?stateId=${estado.id}`)
            .then(({ list }) => {
                setCidadeList(list);
            }).catch(() => {
                setCidadeList([]);
            });
    }

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered, estado, cidade, ativo }) => {
        setLoadingTable(true);

        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
            estado: estado ? estado?.id : tableState.current.estado,
            cidade: cidade ? cidade?.id : tableState.current.cidade,
            ativo: (ativo != null || ativo != undefined || ativo?.id == 1) ? ativo?.value : tableState.current.ativo,
        };

        tableState.current = updatedTableState;

        post("/forum/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
            estado: updatedTableState.estado,
            cidade: updatedTableState.cidade,
            ativo: updatedTableState.ativo,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false)
            setAplicar(false);
        });

    }, []);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
        setUploadArquivo(false);
    }

    const userColumns = [
        {
            header: "nome",
            accessorKey: "nome",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "cidade",
            accessorKey: "municipio.descricao",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "estado",
            accessorKey: "municipio.estado.codEstado",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "ativo",
            accessorKey: "ativo",
            cell: info => (
                <Checkbox
                    checked={info.getValue()}
                    disabled
                    color="primary"
                    inputProps={{ 'aria-label': 'Ativo' }}
                />
            ),
            isSorted: false
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            onClick={() => {
                                setSelectedId(info.row.original.id);
                                setIsCreateEdit(true);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Remover Serviço">
                        <IconButton
                            onClick={() => {
                                handleRemove(info.row.original.id);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="error" sx={{ marginRight: 1 }}>delete</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                </>
            ),
            width: "15%"
        },
    ];

    const handleRemove = (id) => {
        if (id != null) {
            infoAviso(
                "Ao remover o fórum, o mesmo será removido de todas os processos vinculados. Deseja continuar?",
                async () => {
                    await removeForum(id);
                },
                () => {
                    return;
                }
            );
            return;
        }
    };

    const removeForum = async (id) => {
        const service = {
            id: id,
            nome: null,
        };

        post("/forum/delete", service)
            .then((res) => {
                if (res?.id == null) {
                    successAlert("Fórum removido com sucesso!");
                    fetchData({ ...tableState });
                } else {
                    infoWarning("Este fórum já foi utilizado e, por isso, não pode ser removido. Apenas a inativação é permitida.")
                }
            })
            .catch((error) => {
                errorAlert("Erro ao remover fórum!");
            })
    }

    const handleApplyFilter = () => {
        let filtered = [];

        Object.keys(filters).forEach(filterKey => {
            if (verifyVariable(filters[filterKey]) && filterKey == 'nome') {
                filtered.push({ id: filterKey, value: filters[filterKey] });
            }
        })
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered, estado: filters.estado, cidade: filters.cidade, ativo: filters.ativo });
    };

    const handleCleanFilter = () => {
        setFilters({
            nome: '',
            estado: null,
            cidade: null,
            ativo: trueOrFalseEnum[1]
        })

        const campos = {
            pageSize: 10,
            pageIndex: 0,
            sorting: [],
            filtered: [],
            estado: null,
            cidade: null,
            ativo: true
        };
        tableState.current = campos;
        fetchData({ ...tableState });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {uploadArquivo &&
                <Grid item xs={12} md={12}>
                    <UploadArquivoForum
                        closeCreateEdit={backFunction} />
                </Grid>
            }

            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <ForumCreateEdit
                        id={selectedId}
                        closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: (isCreateEdit || uploadArquivo) ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? "#4B4F5C" : "#fafafa",
                        borderColor: darkMode ? "#333" : "#ddd",
                    }}
                >
                    <Grid container spacing={1}>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome</InputLabel>
                                <MDInput
                                    size="small"
                                    placeholder="Informe o nome..."
                                    value={filters.nome}
                                    onChange={({ target }) => setFilters({ ...filters, nome: target.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleApplyFilter();
                                        }
                                    }}
                                    fullWidth
                                />
                            </Grid>


                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Estado</InputLabel>
                                <MDSelect
                                    getOptionValue={option => option.id}
                                    getOptionLabel={option => option.descricao}
                                    name='estado-select'
                                    placeholder='Selecione o estado'
                                    value={filters.estado}
                                    options={estadoList}
                                    onChange={selectedOptions => {
                                        setFilters({
                                            ...filters,
                                            estado: selectedOptions,
                                            cidade: null,
                                        })
                                        selectEstadoCidade(selectedOptions)
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cidade</InputLabel>
                                <MDSelect
                                    getOptionValue={option => option.id}
                                    getOptionLabel={option => option.descricao}
                                    name="cidade-select"
                                    placeholder="Selecione a cidade"
                                    value={filters.cidade}
                                    options={cidadeList}
                                    onChange={selectedOptions => {
                                        setFilters({ ...filters, cidade: selectedOptions })
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Ativo</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={option => option.id}
                                    getOptionLabel={option => option.name}
                                    name="ativo-multi-select"
                                    placeholder="Ambos"
                                    value={filters.ativo}
                                    options={trueOrFalseEnum}
                                    onChange={selectedOptions => {
                                        setFilters({ ...filters, ativo: selectedOptions })
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} display='flex' justifyContent='space-between' pt={1}>
                                <MDBox display="flex" gap={2}>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={() => {
                                            setSelectedId(null);
                                            setIsCreateEdit(true);
                                        }}>
                                        Adicionar fórum
                                    </MDButton>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        onClick={() => {
                                            setUploadArquivo(true);
                                        }}>
                                        UPLOAD ARQUIVO
                                    </MDButton>
                                </MDBox>

                                <MDBox display="flex">
                                    <MDButton
                                        size="medium"
                                        sx={{ marginRight: 2 }}
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={handleCleanFilter}>
                                        Limpar filtro
                                    </MDButton>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={handleApplyFilter}>
                                        Aplicar filtro
                                    </MDButton>
                                </MDBox>
                            </Grid>
                    </Grid>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={userColumns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    changeFilter={aplicar}
                    tableState={tableState} />
            </Grid>
        </Grid >
    );
}
